import { useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import MainCard from "../../components/cards/MainCard";
import {
    useLazyLookupIpQuery,
    useGetCustomRulesQuery,
    useAddCustomRuleMutation,
    useDeleteCustomRuleMutation
} from "../../store/services/waf.service";

// Accepts a plain IPv4 (x.x.x.x) or an IPv4 CIDR (x.x.x.x/0-32)
const isValidIpOrCidr = (value) => {
    const [ip, mask, ...rest] = value.trim().split("/");
    if (rest.length) return false;
    const octets = ip.split(".");
    const ipOk = octets.length === 4 && octets.every((o) => /^\d{1,3}$/.test(o) && Number(o) <= 255);
    if (!ipOk) return false;
    if (mask === undefined) return true;
    return /^\d{1,2}$/.test(mask) && Number(mask) <= 32;
};

const IpLookupSection = () => {
    const [ip, setIp] = useState("");
    const [inputError, setInputError] = useState("");
    const [trigger, { data, error, isFetching }] = useLazyLookupIpQuery();

    const handleSearch = () => {
        const trimmed = ip.trim();
        if (!isValidIpOrCidr(trimmed)) {
            setInputError("Enter a valid IPv4 address, e.g. 85.210.240.134");
            return;
        }
        setInputError("");
        trigger(trimmed);
    };

    const renderResult = () => {
        if (isFetching) {
            return (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress />
                </Box>
            );
        }
        if (error) {
            const message =
                error?.data?.error ||
                (error?.status === 403 ? "You do not have permission to use this tool." : "Something went wrong.");
            return <Alert severity="error" variant="filled">{message}</Alert>;
        }
        if (!data) return null;
        if (data.listed) {
            return (
                <Alert severity="warning" variant="outlined">
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{data.ip} is on the blocklist</Typography>
                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                        <Typography variant="body2">Matched range: <strong>{data.matchedCidr}</strong></Typography>
                        <Typography variant="body2">Source: <Chip label={data.source} size="small" sx={{ ml: 0.5 }} /></Typography>
                        {data.description && <Typography variant="body2">Note: {data.description}</Typography>}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>{data.note}</Typography>
                </Alert>
            );
        }
        return <Alert severity="success" variant="outlined">{data.ip} is not on the blocklist for this environment.</Alert>;
    };

    return (
        <MainCard title="IP Lookup">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Check whether an IP address is on this environment&apos;s WAF blocklist.
            </Typography>
            <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} sm={8} md={6}>
                    <TextField
                        label="IP address"
                        placeholder="85.210.240.134"
                        value={ip}
                        size="small"
                        fullWidth
                        error={!!inputError}
                        helperText={inputError}
                        onChange={(e) => setIp(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    />
                </Grid>
                <Grid item xs={12} sm={4} md={2}>
                    <Button variant="contained" onClick={handleSearch} disabled={isFetching || !ip.trim()} sx={{ height: 40 }} fullWidth>
                        {isFetching ? <CircularProgress size={22} /> : "Search"}
                    </Button>
                </Grid>
                <Grid item xs={12}>{renderResult()}</Grid>
            </Grid>
        </MainCard>
    );
};

const CustomRulesSection = () => {
    const { data: rules, isLoading } = useGetCustomRulesQuery();
    const [addRule, { isLoading: isAdding }] = useAddCustomRuleMutation();
    const [deleteRule] = useDeleteCustomRuleMutation();

    const [ip, setIp] = useState("");
    const [description, setDescription] = useState("");
    const [inputError, setInputError] = useState("");
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const notify = (message, severity = "success") => setSnackbar({ open: true, message, severity });

    const handleAdd = async () => {
        const trimmed = ip.trim();
        if (!isValidIpOrCidr(trimmed)) {
            setInputError("Enter a valid IPv4 address or CIDR, e.g. 203.0.113.4 or 203.0.113.0/24");
            return;
        }
        setInputError("");
        try {
            await addRule({ ip: trimmed, description: description.trim() }).unwrap();
            setIp("");
            setDescription("");
            notify(`Added ${trimmed} to the blocklist.`);
        } catch (e) {
            notify(e?.data?.error || "Failed to add rule.", "error");
        }
    };

    const handleDelete = async (ruleIp) => {
        try {
            await deleteRule(ruleIp).unwrap();
            notify(`Removed ${ruleIp} from the blocklist.`);
        } catch (e) {
            notify(e?.data?.error || "Failed to remove rule.", "error");
        }
    };

    return (
        <MainCard title="Custom Rules" sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Block additional IPs or ranges of your own. Changes apply immediately. Azure and AWS ranges are
                managed automatically and aren&apos;t shown here.
            </Typography>

            <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 1 }}>
                <Grid item xs={12} sm={4}>
                    <TextField
                        label="IP or CIDR"
                        placeholder="203.0.113.0/24"
                        value={ip}
                        size="small"
                        fullWidth
                        error={!!inputError}
                        helperText={inputError}
                        onChange={(e) => setIp(e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} sm={5}>
                    <TextField
                        label="Description (optional)"
                        value={description}
                        size="small"
                        fullWidth
                        onChange={(e) => setDescription(e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Button variant="contained" onClick={handleAdd} disabled={isAdding || !ip.trim()} sx={{ height: 40 }} fullWidth>
                        {isAdding ? <CircularProgress size={22} /> : "Add rule"}
                    </Button>
                </Grid>
            </Grid>

            {isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress /></Box>
            ) : !rules || rules.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No custom rules yet.</Typography>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>IP / CIDR</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell align="right">Remove</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rules.map((rule) => (
                            <TableRow key={rule.ip}>
                                <TableCell>{rule.ip}</TableCell>
                                <TableCell>{rule.description}</TableCell>
                                <TableCell align="right">
                                    <Tooltip title="Remove rule">
                                        <IconButton size="small" color="error" onClick={() => handleDelete(rule.ip)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
            </Snackbar>
        </MainCard>
    );
};

const PortalConfig = () => (
    <Box sx={{ p: 3 }}>
        <IpLookupSection />
        <CustomRulesSection />
    </Box>
);

export default PortalConfig;
